import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AutoSizer, List } from 'react-virtualized';

import { Box, CircularProgress, Typography } from '@mui/material';
import { ArrowIcon, ClockIcon, PhoneMissedIcon } from '@sharedCR/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { TELNYX_SIP_DOMAIN } from 'constants/telephony';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  fetchCallHistoryAction,
  makeOutgoingCallAction,
  setCallHistoryFilterAction,
} from 'store/system/actions';
import {
  selectCallHistoryFilter,
  selectCallHistoryLoading,
  selectFilteredCallHistory,
} from 'store/system/selectors';

import {
  callHistoryContainerStyles,
  emptyStateIconStyles,
  emptyStateStyles,
  emptyStateTextStyles,
  filterChipActiveStyles,
  filterChipStyles,
  filterRowStyles,
  historyDetailStyles,
  historyInfoStyles,
  historyListStyles,
  historyMissedNameStyles,
  historyNameStyles,
  historyRowStyles,
  historyTimeStyles,
  inboundIconStyles,
  loadingContainerStyles,
  missedIconStyles,
  outboundIconStyles,
} from './CallHistory.styles';

const FILTER_OPTIONS = [
  { key: 'all', label: 'All' },
  { key: 'missed', label: 'Missed' },
  { key: 'inbound', label: 'Inbound' },
  { key: 'outbound', label: 'Outbound' },
];

// Row height: 10px padding top + 10px padding bottom + ~20px content = ~50px
const HISTORY_ROW_HEIGHT = 50;

const MS_PER_MINUTE = 60000;
const MS_PER_HOUR = 3600000;
const MS_PER_DAY = 86400000;
const MINUTES_PER_HOUR = 60;
const HOURS_PER_DAY = 24;
const DAYS_PER_WEEK = 7;
const SECONDS_PER_MINUTE = 60;

const formatCallTime = isoString => {
  if (!isoString) {
    return '';
  }

  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / MS_PER_MINUTE);
  const diffHours = Math.floor(diffMs / MS_PER_HOUR);
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffMins < 1) {
    return 'Just now';
  }

  if (diffMins < MINUTES_PER_HOUR) {
    return `${diffMins}m ago`;
  }

  if (diffHours < HOURS_PER_DAY) {
    return `${diffHours}h ago`;
  }

  if (diffDays === 1) {
    return 'Yesterday';
  }

  if (diffDays < DAYS_PER_WEEK) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const formatDuration = seconds => {
  if (!seconds || seconds <= 0) {
    return 'Not answered';
  }

  const mins = Math.floor(seconds / SECONDS_PER_MINUTE);
  const secs = Math.round(seconds % SECONDS_PER_MINUTE);

  if (mins === 0) {
    return `${secs}s`;
  }

  return `${mins}m ${secs}s`;
};

const getDirectionIcon = (typeOfCall, isAnswered) => {
  if (!isAnswered) {
    return <PhoneMissedIcon style={missedIconStyles} />;
  }

  if (typeOfCall === 'Inbound') {
    return <ArrowIcon direction={IconDirection.DOWN} style={inboundIconStyles} />;
  }

  return <ArrowIcon direction={IconDirection.UP} style={outboundIconStyles} />;
};

const HistoryRow = memo(({ entry, onRedial }) => {
  const { typeOfCall, isAnswered, callDateTime, duration, userFirstName, userLastName, number } =
    entry;

  const name = userFirstName ? `${userFirstName} ${userLastName}` : number;
  const detail = formatDuration(duration);
  const time = formatCallTime(callDateTime);
  const isMissed = !isAnswered;

  const handleClick = useCallback(() => {
    onRedial(entry);
  }, [entry, onRedial]);

  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  return (
    <Box
      role="button"
      sx={historyRowStyles}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {getDirectionIcon(typeOfCall, isAnswered)}
      <Box sx={historyInfoStyles}>
        <Typography sx={isMissed ? historyMissedNameStyles : historyNameStyles} variant="regular14">
          {name}
        </Typography>
        <Typography sx={historyDetailStyles} variant="regular12">
          {detail}
        </Typography>
      </Box>
      <Typography sx={historyTimeStyles} variant="regular12">
        {time}
      </Typography>
    </Box>
  );
});

HistoryRow.displayName = 'HistoryRow';

HistoryRow.propTypes = {
  entry: PropTypes.shape({
    typeOfCall: PropTypes.string,
    isAnswered: PropTypes.bool,
    callDateTime: PropTypes.string,
    duration: PropTypes.number,
    userFirstName: PropTypes.string,
    userLastName: PropTypes.string,
    number: PropTypes.string,
    userId: PropTypes.string,
  }).isRequired,
  onRedial: PropTypes.func.isRequired,
};

const FilterChip = memo(({ filterKey, label, isActive, onSelect }) => {
  const handleClick = useCallback(() => {
    onSelect(filterKey);
  }, [filterKey, onSelect]);

  return (
    <Box
      component="button"
      sx={isActive ? filterChipActiveStyles : filterChipStyles}
      type="button"
      onClick={handleClick}
    >
      {label}
    </Box>
  );
});

FilterChip.displayName = 'FilterChip';

FilterChip.propTypes = {
  filterKey: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

const CallHistory = () => {
  const dispatch = useDispatch();
  const history = useSelector(selectFilteredCallHistory);
  const isLoading = useSelector(selectCallHistoryLoading);
  const activeFilter = useSelector(selectCallHistoryFilter);

  useEffectSafeStrictMode(() => {
    dispatch(fetchCallHistoryAction());
  }, [dispatch]);

  const onFilterChange = useCallback(
    filter => {
      dispatch(setCallHistoryFilterAction(filter));
    },
    [dispatch],
  );

  const onRedial = useCallback(
    entry => {
      if (!entry.number) {
        return;
      }

      const destinationNumber = entry.userId
        ? `${entry.number}@${TELNYX_SIP_DOMAIN}`
        : entry.number;

      dispatch(
        makeOutgoingCallAction({
          destinationNumber,
        }),
      );
    },
    [dispatch],
  );

  const renderHistoryRow = useCallback(
    ({ index, key, style }) => {
      const entry = history[index];

      return (
        <div key={key} style={style}>
          <HistoryRow entry={entry} onRedial={onRedial} />
        </div>
      );
    },
    [history, onRedial],
  );

  const noHistoryRenderer = useCallback(
    () => (
      <Box sx={emptyStateStyles}>
        <Box sx={emptyStateIconStyles}>
          <ClockIcon size={IconSize[48]} />
        </Box>
        <Typography sx={emptyStateTextStyles} variant="regular14">
          {activeFilter === 'all' ? 'No call history yet' : `No ${activeFilter} calls`}
        </Typography>
      </Box>
    ),
    [activeFilter],
  );

  const filterChips = useMemo(
    () =>
      FILTER_OPTIONS.map(option => (
        <FilterChip
          filterKey={option.key}
          isActive={activeFilter === option.key}
          key={option.key}
          label={option.label}
          onSelect={onFilterChange}
        />
      )),
    [activeFilter, onFilterChange],
  );

  if (isLoading) {
    return (
      <Box sx={callHistoryContainerStyles}>
        <Box sx={loadingContainerStyles}>
          <CircularProgress size={32} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={callHistoryContainerStyles}>
      <Box sx={filterRowStyles}>{filterChips}</Box>

      <Box sx={historyListStyles}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              height={height}
              noRowsRenderer={noHistoryRenderer}
              overscanRowCount={5}
              rowCount={history.length}
              rowHeight={HISTORY_ROW_HEIGHT}
              rowRenderer={renderHistoryRow}
              width={width}
            />
          )}
        </AutoSizer>
      </Box>
    </Box>
  );
};

export default memo(CallHistory);
